import {SvgIcon} from "@mui/material";
import {animated, useSpring} from "@react-spring/web";
import Collapse from "@mui/material/Collapse";
import React, {useCallback, useMemo} from "react";
import {TreeItem, treeItemClasses} from "@mui/x-tree-view/TreeItem";
import {styled} from "@mui/material/styles";
import {TreeView} from "@mui/x-tree-view/TreeView";
import CategoryManagementViewCategoryCard from "../category-card";
import classnames from "classnames";

function MinusSquare(props) {
    return (
        <SvgIcon fontSize="inherit" style={{width: 14, height: 14}} {...props} color={'secondary'}>
            <path
                d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z"/>
        </SvgIcon>
    );
}

function PlusSquare(props) {
    return (
        <SvgIcon fontSize="inherit" style={{width: 14, height: 14}} {...props} color={'secondary'}>
            <path
                d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z"/>
        </SvgIcon>
    );
}

function TransitionComponent(props) {
    const style = useSpring({
        to: {
            opacity: props.in ? 1 : 0,
            transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
        },
    });

    return (
        <animated.div style={style}>
            <Collapse {...props} />
        </animated.div>
    );
}

const CustomTreeItem = React.forwardRef((props, ref) => (
    <TreeItem {...props} TransitionComponent={TransitionComponent} ref={ref}/>
));

const StyledTreeItem = styled(CustomTreeItem)(({theme}) => ({
    marginBlock: 4,
    borderRadius: 8,
    '&:not(.root)': {
        [`& .${treeItemClasses.content}`]: {
            position: 'relative',
            '&:before': {
                content: '""',
                position: 'absolute',
                borderBottom: `2px dashed ${theme.palette.divider}`,
                top: '50%',
                left: -36,
                width: 44,
            },
        },
        [`&:not(.with-children)`]: {
            [`& .${treeItemClasses.content}`]: {
                '&:before': {
                    width: 66,
                },
            },
        },
    },
    [`& .${treeItemClasses.iconContainer}`]: {
        cursor: 'pointer',
    },
    [`& .${treeItemClasses.content}`]: {
        borderRadius: 8,
        paddingBlock: 8,
        cursor: 'default',
        [`&:hover, &.${treeItemClasses.focused}`]: {
            backgroundColor: 'transparent',
        },
        [`& .${treeItemClasses.label}`]: {
            width: 280,
        },
    },
    '&.create-new': {
        [`& .${treeItemClasses.content}`]: {
            [`& .${treeItemClasses.label}`]: {
                width: 230,
            },
        }
    },
    [`& .${treeItemClasses.group}`]: {
        marginLeft: 15,
        paddingLeft: 36,
        // borderLeft: `1px dashed ${theme.palette.divider}`,
        position: 'relative',
        '&:before': {
            content: '""',
            position: 'absolute',
            top: -44,
            bottom: 34,
            left: -1,
            borderLeft: `2px dashed ${theme.palette.divider}`,
        },
    },
}));

const CategoriesTreeView = ({
                                categories,
                                categoriesFlat,
                                upsert,
                                removingIds,
                                remove,
                                toggleActive,
                            }) => {
    const defaultExpanded = useMemo(() => [], []);
    const leafCategories = useMemo(() => categoriesFlat?.filter((c, index, array) => !array.some(e => e.parentId === c.id)) ?? [], [categoriesFlat]);

    const renderItems = useCallback((parent, items, parentColor = null, depth = 0) => {
        return [
            ...items?.map(item => {
                return (
                    <StyledTreeItem
                        key={item.id}
                        nodeId={item.id.toString()}
                        className={classnames({'root': !parent, 'with-children': true})}
                        label={
                            <CategoryManagementViewCategoryCard
                                parentColor={!parent ? item.color : parentColor}
                                depth={depth}
                                category={item}
                                siblingCategories={items.filter(i => i.id !== item.id)}
                                leafCategories={leafCategories}
                                parent={parent}
                                toggleActive={toggleActive}
                                upsertCategory={upsert}
                                removeCategory={remove}
                                removing={removingIds.includes(item.id)}
                            />
                        }
                    >
                        {renderItems(item, item.children ?? [], !parent ? item.color : parentColor, depth + 1)}
                    </StyledTreeItem>
                )
            }),
            <StyledTreeItem
                key={`${parent?.id ?? 'root'}-create-new`}
                nodeId={`${parent?.id ?? 'root'}-create-new`}
                className={classnames({'root': !parent, 'create-new': true})}
                label={<CategoryManagementViewCategoryCard upsertCategory={upsert} parent={parent}/>}
            />
        ]
    }, [remove, removingIds, toggleActive, upsert, leafCategories])

    return (
        <TreeView
            disableSelection
            disabledItemsFocusable
            defaultExpanded={defaultExpanded}
            defaultCollapseIcon={<MinusSquare/>}
            defaultExpandIcon={<PlusSquare/>}
            defaultEndIcon={<React.Fragment/>}
            sx={{minWidth: '100%', width: 'fit-content'}}
        >
            {renderItems(null, categories ?? [])}
        </TreeView>
    );
}

export default CategoriesTreeView;
